import { SubscriptionTier } from '@heidi/schema/ml-scribe';

import type { CreditAllowanceNudgeVariant } from '../../../data/query/credit-allowance.utils';
import type { CreditUsagePopoverPlan } from './types';

/** The upgrade-nudge matrix rows that have a sidebar usage card. */
export type UsageNudgeCell =
  | 'trial'
  | 'free'
  | 'clinicianStandard'
  | 'clinicianMax'
  | 'practiceStandardAdmin'
  | 'practiceStandardMember';

/** Only these two usage levels open the card. */
export type UsageNudgeThreshold = 90 | 100;

export const CLINICIAN_UPGRADE_CHECKOUT_ROUTE = '/settings/billing/clinician-upgrade';
export const TEAM_UPGRADE_CHECKOUT_ROUTE = '/settings/billing/upgrade';

export type UsageNudgeCellInput = {
  tier: SubscriptionTier | null | undefined;
  isMaxSeat: boolean;
  isTrialing: boolean;
  isComplimentaryAllowance: boolean;
  /** `undefined` while the team role is still loading. */
  isTeamAdmin: boolean | undefined;
};

/**
 * Which matrix row the member sits in, or `null` when they get no card: an
 * uncapped complimentary cycle, Practice Max, legacy and enterprise tiers, or a
 * Practice member whose role has not resolved yet.
 */
export function resolveUsageNudgeCell({
  tier,
  isMaxSeat,
  isTrialing,
  isComplimentaryAllowance,
  isTeamAdmin,
}: UsageNudgeCellInput): UsageNudgeCell | null {
  if (isComplimentaryAllowance) return null;
  if (isTrialing) return 'trial';
  switch (tier) {
    case SubscriptionTier.FREE:
      return 'free';
    case SubscriptionTier.CLINICIAN_PRO:
      return isMaxSeat ? 'clinicianMax' : 'clinicianStandard';
    case SubscriptionTier.CLINICIAN_PRO_PLUS:
      if (isMaxSeat || isTeamAdmin == null) return null;
      return isTeamAdmin ? 'practiceStandardAdmin' : 'practiceStandardMember';
    default:
      return null;
  }
}

export function resolveUsageNudgeThreshold(
  variant: CreditAllowanceNudgeVariant | null
): UsageNudgeThreshold | null {
  return variant === 90 || variant === 100 ? variant : null;
}

export type UsageNudgeCta =
  | { kind: 'checkout'; href: string }
  | { kind: 'pricingPlans' }
  | { kind: 'requestUpgrade' };

/**
 * Where the card's button goes. The individual seat checkout turns team members
 * away and the team checkout sits behind its own flag, so both fall back to the
 * pricing plans modal.
 */
export function resolveUsageNudgeCta(
  cell: UsageNudgeCell,
  {
    hasTeam,
    isTeamUpgradesEnabled,
    isRequestUpgradeAvailable,
  }: {
    hasTeam: boolean;
    /** `release-heidi-2-team-upgrades`. */
    isTeamUpgradesEnabled: boolean;
    /** The member may send a seat upgrade request to their admin. */
    isRequestUpgradeAvailable: boolean;
  }
): UsageNudgeCta | null {
  switch (cell) {
    case 'trial':
    case 'free':
      return hasTeam
        ? { kind: 'pricingPlans' }
        : { kind: 'checkout', href: `${CLINICIAN_UPGRADE_CHECKOUT_ROUTE}?seat=standard` };
    case 'clinicianStandard':
      return hasTeam
        ? { kind: 'pricingPlans' }
        : { kind: 'checkout', href: `${CLINICIAN_UPGRADE_CHECKOUT_ROUTE}?seat=max` };
    case 'clinicianMax':
    case 'practiceStandardAdmin':
      return isTeamUpgradesEnabled
        ? { kind: 'checkout', href: TEAM_UPGRADE_CHECKOUT_ROUTE }
        : { kind: 'pricingPlans' };
    case 'practiceStandardMember':
      return isRequestUpgradeAvailable ? { kind: 'requestUpgrade' } : null;
  }
}

export type UsageNudgeTrigger = {
  /** Blue filled tile even though no trial countdown is running. */
  forceTile: boolean;
  /** Icon to show instead of the member's own plan. */
  iconPlan?: CreditUsagePopoverPlan;
};

/** Button rules on top of today's ones: every tier with a card turns blue at 90%. */
export function resolveUsageNudgeTrigger(
  cell: UsageNudgeCell | null,
  threshold: UsageNudgeThreshold | null
): UsageNudgeTrigger {
  return {
    forceTile: cell != null && threshold != null,
    ...(cell === 'clinicianMax' && { iconPlan: 'practiceMax' as const }),
  };
}
